import { useCallback, useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { asUserId, type UserId } from '@leanin/shared/ids';
import { useMember } from '@/lib/use-session';
import { getApiClient } from '@/lib/api-client';
import { useDmReachability } from '@/lib/queries/messaging';
import { MessagesClient } from './-components/MessagesClient';

export type MessagesTab = 'all' | 'unread' | 'requests';

type MessagesSearch = {
  c?: string;
  compose?: string;
  draft?: string;
  q?: string;
  tab?: MessagesTab;
};

/**
 * `compose` sentinel meaning "open the composer with no recipient selected"
 * — the share-to-Circle entry point, where the viewer picks who to send to.
 * Every other `compose` value is a member id that find-or-creates a DM, so
 * the sentinel is deliberately not a UUID: the `isUuid` gate below rejects
 * it and no conversation is created until the viewer chooses a recipient.
 */
export const COMPOSE_NEW = 'new';

/**
 * The options every `/messages` link is built from.
 *
 * Pass either an existing `conversationId` (lands on that thread), a
 * `composeUserId` (route find-or-creates a DM with that user), or
 * `composeNew` (opens the composer with no recipient, for the viewer to
 * choose). Optional `draft` seeds the new conversation's composer with a
 * body — used by the coach rail's share-takeaways action. Optional
 * `messageId` adds the deep-link hash so the client scrolls to a specific
 * message. Optional `tab` selects the inbox scope (`all` is the default and
 * is left out of the URL so the canonical link stays clean).
 */
export type MessagesLinkOpts = {
  readonly conversationId?: string;
  readonly composeUserId?: string;
  readonly composeNew?: boolean;
  readonly draft?: string;
  readonly messageId?: string;
  readonly tab?: MessagesTab;
};

/**
 * Single source of truth for the `/messages` search-param shape. All callers
 * route through here (or `messagesHref`, which serializes this) so the params
 * stay in lockstep with `validateSearch` below — a future rename ripples
 * through one file, not the codebase.
 *
 * Use this with `navigate({ to: '/messages', search: messagesSearch(...) })`.
 * Prefer the search object over a pre-built `messagesHref` string: on the old
 * router (≤1.95) the string round trip dropped percent-encoded newlines and
 * silently flattened the multi-line `draft` body. Today's router preserves
 * them (pinned by messages-link.test.ts), so the href form is safe too — the
 * search-object form stays canonical because it typechecks the params.
 */
export function messagesSearch(opts: MessagesLinkOpts): MessagesSearch {
  return {
    ...(opts.conversationId ? { c: opts.conversationId } : {}),
    ...(opts.composeUserId
      ? { compose: opts.composeUserId }
      : opts.composeNew
        ? { compose: COMPOSE_NEW }
        : {}),
    ...(opts.draft ? { draft: opts.draft } : {}),
    ...(opts.tab !== undefined && opts.tab !== 'all' ? { tab: opts.tab } : {}),
  };
}

/**
 * The same link as an href string, for anchors and `<Link to>` targets that
 * take one. Serializes `messagesSearch` and appends the deep-link hash.
 */
export function messagesHref(opts: MessagesLinkOpts): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(messagesSearch(opts))) {
    if (value !== undefined) params.set(key, value);
  }
  const qs = params.toString();
  const hash = opts.messageId ? `#message-${opts.messageId}` : '';
  return `/messages${qs ? `?${qs}` : ''}${hash}`;
}

function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

// `all` is the implicit default and is never written to the URL, so only the
// two non-default scopes round-trip through the search param.
function pickTab(v: unknown): Exclude<MessagesTab, 'all'> | undefined {
  return v === 'unread' || v === 'requests' ? v : undefined;
}

export const Route = createFileRoute('/_authed/messages/')({
  validateSearch: (raw: Record<string, unknown>): MessagesSearch => ({
    ...(pickStr(raw.c) ? { c: pickStr(raw.c) as string } : {}),
    ...(pickStr(raw.compose) ? { compose: pickStr(raw.compose) as string } : {}),
    ...(pickStr(raw.draft) ? { draft: pickStr(raw.draft) as string } : {}),
    ...(pickStr(raw.q) ? { q: pickStr(raw.q) as string } : {}),
    ...(pickTab(raw.tab) ? { tab: pickTab(raw.tab) } : {}),
  }),
  component: MessagesPage,
});

async function findOrCreateDirectConversation(userId: string): Promise<string | null> {
  const client = getApiClient();
  const { data, error, response } = await client.POST('/api/v1/conversations', {
    body: { kind: 'dm', userIds: [asUserId(userId)] },
  });
  if (data !== undefined) return data.data.id;
  if (response.status === 409) {
    const body = error as { error?: { details?: { conversationId?: string } } } | undefined;
    const existing = body?.error?.details?.conversationId;
    if (existing !== undefined) return existing;
  }
  return null;
}

function MessagesPage() {
  const search = Route.useSearch();
  const member = useMember();
  const navigate = useNavigate({ from: Route.fullPath });

  // A failed find-or-create must not die silently: the one-shot ref below
  // never re-arms, so without a fallback a transient 5xx turned a profile
  // "Message" click into an empty inbox with no error and no retry. Handing
  // the target to the compose modal instead reuses its Start button — a
  // visible, retryable path with its own error display.
  const [composeFallbackUserId, setComposeFallbackUserId] = useState<UserId | null>(null);

  const composeMutation = useMutation({
    mutationFn: (userId: string) => findOrCreateDirectConversation(userId),
    onSuccess: (conversationId, userId) => {
      // Hand the resolved thread to the client through the URL — `c` is the
      // single source of truth for the open thread (MessagesClient mirrors
      // its selection back into it). `compose` is spent: leaving it in place
      // would re-fire the find-or-create on refresh.
      if (conversationId !== null) {
        void navigate({
          to: '/messages',
          search: (prev) => {
            const { compose: _spent, ...rest } = prev;
            return { ...rest, c: conversationId };
          },
          replace: true,
        });
        return;
      }
      // Non-409 error response (the helper returns null rather than throwing).
      setComposeFallbackUserId(asUserId(userId));
    },
    onError: (_err, userId) => setComposeFallbackUserId(asUserId(userId)),
  });

  const compose = search.compose;
  const shouldCompose =
    compose !== undefined && isUuid(compose) && member !== null && compose !== member.id;

  // Before creating anything, ask whether a DM to this member would land as a
  // pending request. If it would, the compose modal opens pre-filled instead
  // (via `composePrefillUserId`), so the viewer sees the "message request"
  // notice and creates the conversation explicitly — a profile "Message"
  // click must never silently file a request in someone's queue. When the
  // gate is off (or the viewer is staff) the server answers `false` and the
  // fast path below creates/opens the thread as before.
  const reachabilityQuery = useDmReachability(shouldCompose ? asUserId(compose) : null);
  const composeWouldBeRequest = reachabilityQuery.data?.wouldBeRequest === true;
  // On a reachability error, fall back to direct creation rather than leaving
  // the Message click dead — the server re-classifies at create time anyway.
  const reachabilityResolved = reachabilityQuery.data !== undefined || reachabilityQuery.isError;

  // Kick off compose-conversation creation once per target. The ref (not the
  // mutation's own state) guards the double-fire window where two effect runs
  // land before `isPending` flips. The mutation's onSuccess rewrites the URL
  // (`compose` → `c`), which is how MessagesClient learns about the thread.
  const composeStartedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!shouldCompose || compose === undefined) return;
    if (!reachabilityResolved || composeWouldBeRequest) return;
    if (composeStartedForRef.current === compose) return;
    composeStartedForRef.current = compose;
    composeMutation.mutate(compose);
  }, [shouldCompose, compose, reachabilityResolved, composeWouldBeRequest, composeMutation]);

  const initialId = search.c ?? null;

  // Both compose entry points hand the client an "open the modal" signal: a
  // member id whose DM would land as a request (so the notice is seen), or
  // the recipient-less sentinel. `draft` rides along either way and is spent
  // by the client once the composer has taken it.
  const spendDraft = useCallback(() => {
    void navigate({
      to: '/messages',
      search: (prev) => {
        const { draft: _spent, compose: _alsoSpent, ...rest } = prev;
        return rest;
      },
      replace: true,
    });
  }, [navigate]);

  return (
    <MessagesClient
      initialId={initialId}
      composePrefillUserId={
        shouldCompose && composeWouldBeRequest ? asUserId(compose) : composeFallbackUserId
      }
      onComposeClosed={() => setComposeFallbackUserId(null)}
      composeNew={compose === COMPOSE_NEW}
      initialDraft={search.draft ?? null}
      onDraftApplied={spendDraft}
    />
  );
}
